import {ZIP_CODE_REGEXP} from '@gfiber/typesmith/regexp';
import {US_STATES} from '@gfiber/typesmith/us_states';
import {isValidPhoneNumber} from 'libphonenumber-js';
import {z} from 'zod';
import * as constants from './constants';
import {YES} from './i18n';
import {formatISODate} from './utils';

const stringBase = z.string().trim();

export const requiredString = (max?: number) => {
  const schema = stringBase.min(1, {
    message: constants.VALIDATION_MESSAGES.REQUIRED,
  });
  return max === undefined ? schema : schema.max(max);
};

export const optionalString = (max?: number) => {
  const schema = max === undefined ? stringBase : stringBase.max(max);
  return schema.optional();
};

export function requiredChoice<T extends readonly [string, ...string[]]>(
  options: T,
) {
  return z
    .enum(options)
    .or(z.literal(''))
    .refine(val => val !== '', {
      message: constants.VALIDATION_MESSAGES.REQUIRED,
    });
}

export function optionalChoice<T extends readonly [string, ...string[]]>(
  options: T,
) {
  return z.enum(options).or(z.literal('')).optional();
}

const US_STATE_CODES = US_STATES.map(s => s.code) as [string, ...string[]];
export const requiredState = requiredChoice(US_STATE_CODES);
export const optionalState = optionalChoice(US_STATE_CODES);

const emailBase = z.email({
  message: constants.VALIDATION_MESSAGES.INVALID_EMAIL,
});
export const requiredEmail = requiredString(254).pipe(emailBase);
export const optionalEmail = optionalString(254).refine(
  val => !val || emailBase.safeParse(val).success,
  {
    message: constants.VALIDATION_MESSAGES.INVALID_EMAIL,
  },
);

export const requiredZipCode = requiredString().regex(ZIP_CODE_REGEXP, {
  message: constants.VALIDATION_MESSAGES.INVALID_ZIP_CODE,
});
export const optionalZipCode = optionalString().refine(
  val => !val || ZIP_CODE_REGEXP.test(val),
  {
    message: constants.VALIDATION_MESSAGES.INVALID_ZIP_CODE,
  },
);

export const requiredPhoneNumber = requiredString().refine(
  val => isValidPhoneNumber(val, 'US'),
  {
    message: constants.VALIDATION_MESSAGES.INVALID_PHONE,
  },
);
export const optionalPhoneNumber = optionalString().refine(
  val => !val || isValidPhoneNumber(val, 'US'),
  {
    message: constants.VALIDATION_MESSAGES.INVALID_PHONE,
  },
);

export const requiredNumber = requiredString(10).regex(/^\d+$/, {
  message: constants.VALIDATION_MESSAGES.ONLY_DIGITS,
});

const dateBase = z.iso.date({
  message: constants.VALIDATION_MESSAGES.INVALID_DATE,
});
export const requiredDate = dateBase;
export const optionalDate = z.union([dateBase, z.literal('')]).optional();
export const optionalDateNotFuture = z
  .union([
    dateBase.refine(val => val <= formatISODate(new Date()), {
      message: constants.VALIDATION_MESSAGES.DATE_CANNOT_BE_FUTURE,
    }),
    z.literal(''),
  ])
  .optional();

export const requiredCheckbox = requiredChoice([YES] as const);

const fileBase = z
  .file()
  .max(
    constants.MAX_FILE_SIZE_BYTES,
    constants.VALIDATION_MESSAGES.FILE_TOO_LARGE,
  )
  .mime(
    constants.ACCEPTED_FILE_TYPES,
    constants.VALIDATION_MESSAGES.INVALID_MIME_TYPE,
  );
export const requiredFileList = z
  .array(fileBase)
  .min(1, {message: constants.VALIDATION_MESSAGES.REQUIRED});
export const optionalFileList = z.array(fileBase).optional();
